import type { TicketType } from '@pulsify/shared/dto'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import type { EventPrice } from '~/shared/types/event'

export const eventEntryValues = ['free', 'tickets', 'signup', 'link'] as const
export type EventEntry = (typeof eventEntryValues)[number]

type EntryValues = {
  entry: EventEntry | null
  price: EventPrice
  ticketUrl: string
  ticketTypes: TicketTypeInput[]
}

/** The price guests see for these ticket types; a free ticket with a door price counts that. */
export function ticketTypesPrice(types: TicketTypeInput[]): EventPrice {
  if (types.length === 0) return { kind: 'unknown' }
  const cents = types.map((type) =>
    type.priceCents > 0 ? type.priceCents : (type.doorPriceCents ?? 0),
  )
  const min = Math.min(...cents)
  const max = Math.max(...cents)
  if (max === 0) return { kind: 'free' }
  if (min === max) return { kind: 'fixed', priceFixed: min / 100 }
  return { kind: 'range', priceRangeMin: min / 100, priceRangeMax: max / 100 }
}

export function applyEventEntry<T extends EntryValues>(values: T): T {
  switch (values.entry) {
    case 'free':
      return { ...values, price: { kind: 'free' }, ticketUrl: '' }
    case 'tickets':
      return { ...values, price: ticketTypesPrice(values.ticketTypes), ticketUrl: '' }
    case 'signup':
      return { ...values, ticketUrl: '' }
    default:
      return values
  }
}

export function entryFromHandoff(handoff: {
  signup: boolean
  ticketUrl: string
  price: EventPrice
}): EventEntry | null {
  if (handoff.ticketUrl.trim()) return 'link'
  if (handoff.signup) return 'signup'
  if (handoff.price.kind === 'free') return 'free'
  return null
}

export function hasPaidTicketTypes(types: TicketTypeInput[]): boolean {
  return types.some((type) => type.priceCents > 0)
}

export function draftTicketType(input: TicketTypeInput, index: number): TicketType {
  return {
    id: `draft-${index}`,
    eventId: '',
    name: input.name,
    priceCents: input.priceCents,
    quantity: input.quantity ?? null,
    soldCount: 0,
    isStudent: input.isStudent ?? false,
    doorPriceCents: input.doorPriceCents ?? null,
    salesEndAt: input.salesEndAt ?? null,
    active: true,
    sort: index,
  }
}
